import { ApolloClient } from '@apollo/client'
import ramdaPipe from 'ramda/src/pipe'
import {
  BehaviorSubject,
  combineLatest,
  defer,
  from,
  map,
  Observable,
  of,
  Subject,
  tap,
} from 'rxjs'
import { catchError, skipWhile, switchMap, take } from 'rxjs/operators'

import {
  CartFragment,
  PlaceOrderDocument,
  PlaceOrderInput,
  PlaceOrderMutation,
  PlaceOrderMutationVariables,
  PlaceOrderWithPaymentMethodDocument,
  PlaceOrderWithPaymentMethodMutation,
  PlaceOrderWithPaymentMethodMutationVariables,
} from '../../../graphql/magento'
import { setLocalStorageState } from '../../../hooks/useStorage'
import { CustomError, InvalidResponseError } from '../../../utils/errorTools'
import { coerceCaughtToLeft } from '../../../utils/rx/errors'
import { mapRight, switchMapRight } from '../../../utils/rx/operators'
import { latestAuthStateWithAccessToken$ } from '../../auth/state'
import { CompletedCart } from '../../completed-carts/types'
import { latestApolloClient$ } from '../../graphql/apollo'
import { UnexpectedCartError } from '../errors'
import { latestCustomerCart$ } from './cart'

export type PlaceOrderTaskResult =
  | {
      _tag: 'Left'
      error: Error
    }
  | {
      _tag: 'Right'
      data: CompletedCart
    }

export class PaymentDetailsMissingError extends CustomError {
  override name = 'PaymentDetailsMissingError'
}

export class PlaceOrderTaskMissingError extends CustomError {
  override name = 'PlaceOrderTaskMissingError'
}

export interface PlaceOrderTask {
  // supply `cart` to validate task before running (compare with cartId bound to task) and
  // produce `CompletedCart` on success
  effect: (
    cart: CartFragment,
    dispensarySlug: string,
    guestMarketingOptIn?: boolean,
    guestOptedInSmsNumber?: string,
  ) => Observable<PlaceOrderTaskResult>
}

const orderPlacedTick$ = new Subject<void>()

/**
 * Ticks once per placed order, the moment the mutation comes back `Right`.
 *
 * Exposed so an effect can retire state that the order makes obsolete without touching the order
 * itself.
 */
export const orderPlaced$ = orderPlacedTick$.asObservable()

/** Parameters applied when an order is placed, rather than bound to the task when it is built. */
export interface PlaceOrderTaskParams {
  cart: CartFragment
  dispensarySlug: string
  guestMarketingOptIn?: boolean
  guestOptedInSmsNumber?: string
}

// True from the moment an order is submitted until the emptied cart has been loaded. Placing an
// order clears the cart in Magento, so consumers that react to an empty cart must ignore it while
// this is set — read it from React with `hooks/usePlaceOrderInProgress`.
export const placeOrderTaskApplicationLoading$ = new BehaviorSubject(false)

/**
 * Run a place order task once, driving `placeOrderTaskApplicationLoading$` and `orderPlaced$`.
 * Shared by the checkout machine's order chain and express checkout, each of which builds its own
 * task; it places an order only for the subscription that runs it.
 */
export const runPlaceOrderTask$ = (
  placeOrderTask: PlaceOrderTask | undefined,
  { cart, dispensarySlug, guestMarketingOptIn, guestOptedInSmsNumber }: PlaceOrderTaskParams,
): Observable<PlaceOrderTaskResult> =>
  defer(() => {
    placeOrderTaskApplicationLoading$.next(true)
    return !placeOrderTask
      ? // safety check: this should never happen
        of({ _tag: 'Left' as const, error: new PlaceOrderTaskMissingError() })
      : placeOrderTask
          .effect(cart, dispensarySlug, guestMarketingOptIn, guestOptedInSmsNumber)
          .pipe(
            tap((result) => {
              if (result._tag === 'Right') {
                orderPlacedTick$.next()
                // order placed, wait for cart to update before setting loading to false
                latestCustomerCart$
                  .pipe(
                    skipWhile(
                      (customerCart) =>
                        customerCart._tag === 'Right' && customerCart.data.customerCart === cart,
                    ),
                    take(1),
                  )
                  .subscribe(() => {
                    placeOrderTaskApplicationLoading$.next(false)
                  })
                return
              }
              // task failed, set loading to false
              placeOrderTaskApplicationLoading$.next(false)
            }),
          )
  })

/** Cart a place order task is bound to, validated against the live cart before the task runs. */
export interface PlaceOrderTaskCart {
  cartId: string
}

/**
 * Assemble the `placeOrder` input, including the guest-only fields for a guest alone.
 *
 * Magento accepts `guest_marketing_opt_in` and `guest_opted_in_sms_number` to record consent
 * collected at checkout from someone with no account, so neither means anything on an authenticated
 * order. Both therefore hang off the same `isGuest` that decides whether the request carries a
 * token, rather than off a caller remembering to leave them out. This is also why the mutations pass
 * the whole input as one variable: a field named in the document is sent on every order and fails
 * validation outright against an instance that does not define it, which is what `m2-front-dev-ca`
 * does with `guest_opted_in_sms_number`.
 *
 * An absent SMS number and an empty one mean different things, so they are sent differently. Absent
 * says we never asked, either because the customer is authenticated or because the market does not
 * offer the opt-in, and the field is left out entirely. Empty says a guest was asked and declined,
 * which Magento forwards to NetSuite where it clears any mobile number held from an earlier order
 * (`updateGuestOptIns` in `netsuite-acct-cust`), so it is sent as-is. The number comes from a
 * free-form `tel` input, hence the trim.
 */
export const placeOrderInputFor = ({
  cartId,
  dispensarySlug,
  isGuest,
  guestMarketingOptIn,
  guestOptedInSmsNumber,
}: PlaceOrderTaskCart & {
  dispensarySlug: string
  isGuest: boolean
  guestMarketingOptIn?: boolean
  guestOptedInSmsNumber?: string
}): PlaceOrderInput => ({
  cart_id: cartId,
  dispensary_slug: dispensarySlug,
  ...(isGuest && guestMarketingOptIn != null
    ? { guest_marketing_opt_in: guestMarketingOptIn }
    : {}),
  ...(isGuest && guestOptedInSmsNumber != null
    ? { guest_opted_in_sms_number: guestOptedInSmsNumber.trim() }
    : {}),
})

/**
 * Place order without payment method mutation (for purchase order)
 */
const placeOrderFactory = (
  client: ApolloClient<unknown>,
  token: string | undefined,
  variables: PlaceOrderMutationVariables,
) =>
  from(
    client.mutate({
      mutation: PlaceOrderDocument,
      variables,
      context: { token },
      errorPolicy: 'none',
    }),
  ).pipe(
    // ignore `errors` in FetchResult since `errorPolicy` is `none` (Apollo will throw if errors returned from server
    map(({ data }) => ({ _tag: 'Right' as const, data })),
    tap(() => {
      // invalidate customerCart query unless token is undefined (guest user)
      if (token) {
        client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'customerCart' })
        client.cache.gc()
      } else {
        // Use the storage state system to properly trigger reactive updates
        setLocalStorageState('guest_cart_id', null)
      }
    }),
    catchError(coerceCaughtToLeft),
  )

/**
 * Place order with payment method mutation (for credit card). Supports sending CVV when placing order.
 */
const placeOrderWithPaymentMethodFactory = (
  client: ApolloClient<unknown>,
  token: string | undefined,
  variables: PlaceOrderWithPaymentMethodMutationVariables,
) =>
  from(
    client.mutate({
      mutation: PlaceOrderWithPaymentMethodDocument,
      variables,
      context: { token },
      errorPolicy: 'none',
    }),
  ).pipe(
    // ignore `errors` in FetchResult since `errorPolicy` is `none` (Apollo will throw if errors returned from server
    map(({ data }) => ({ _tag: 'Right' as const, data })),
    tap(() => {
      // vary cache invalidation based on whether user is authenticated
      if (token) {
        // Drop the saved-card list: completing the order stamps the used card's `last_use`, which is
        // what makes it selectable (see isSelectableTokenBaseCard). Deferred because an immediate
        // evict makes the still-mounted payment section refetch before Magento stamps the card
        // (observed ~2s after the order response), re-caching it as unselectable. By the delay the
        // navigation to confirmation has typically unmounted the payment section — so no refetch
        // fires at all and the next subscriber fetches fresh — and any observer that does remain
        // refetches post-stamp; cache-and-network still re-validates on the next subscribe either way.
        setTimeout(() => {
          client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'tokenBaseCards' })
          client.cache.gc()
        }, 2000)
        client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'customerCart' })
        client.cache.gc()
      } else {
        // Use the storage state system to properly trigger reactive updates
        setLocalStorageState('guest_cart_id', null)
      }
    }),
    catchError(coerceCaughtToLeft),
  )

type ValidPlaceOrderMutation = PlaceOrderMutation & {
  placeOrder: NonNullable<PlaceOrderWithPaymentMethodMutation['placeOrder']>
}

type ValidPlaceOrderWithPaymentMethodMutation = PlaceOrderWithPaymentMethodMutation & {
  placeOrder: NonNullable<PlaceOrderWithPaymentMethodMutation['placeOrder']>
  setPaymentMethodOnCart: NonNullable<PlaceOrderWithPaymentMethodMutation['setPaymentMethodOnCart']>
}

function isValidPlaceOrderMutation(
  data: PlaceOrderMutation | null | undefined,
): data is ValidPlaceOrderMutation {
  return Boolean(data?.placeOrder?.order)
}

function isValidPlaceOrderWithPaymentMethodMutation(
  data: PlaceOrderWithPaymentMethodMutation | null | undefined,
): data is ValidPlaceOrderWithPaymentMethodMutation {
  return Boolean(data?.placeOrder?.order) && Boolean(data?.setPaymentMethodOnCart?.cart)
}

/**
 * Create task to request placeOrder mutation by itself (for free or purchase order payment methods)
 */
export const createVanillaPlaceOrderTask: (params: PlaceOrderTaskCart) => PlaceOrderTask =
  ramdaPipe(
    (params) => (cartToValidate: CartFragment) =>
      of(
        params.cartId === cartToValidate.id
          ? { _tag: 'Right' as const, data: params }
          : {
              _tag: 'Left' as const,
              error: new UnexpectedCartError(
                `Cart ID differs from state (expected: ${params.cartId}, actual: ${cartToValidate.id})`,
              ),
            },
      ),
    (validateParams) => (cart, dispensarySlug, guestMarketingOptIn, guestOptedInSmsNumber) =>
      validateParams(cart).pipe(
        switchMapRight(({ data: params }) =>
          combineLatest([
            latestApolloClient$,
            // use latestAuthStateWithAccessToken$ to ensure authentication is settled
            latestAuthStateWithAccessToken$,
          ]).pipe(
            take(1),
            switchMap(([client, authState]) =>
              placeOrderFactory(
                client,
                authState.isAuthenticated ? authState.accessToken : undefined,
                {
                  input: placeOrderInputFor({
                    cartId: params.cartId,
                    dispensarySlug,
                    isGuest: !authState.isAuthenticated,
                    guestMarketingOptIn,
                    guestOptedInSmsNumber,
                  }),
                },
              ).pipe(
                mapRight(({ data }) =>
                  !isValidPlaceOrderMutation(data)
                    ? {
                        _tag: 'Left' as const,
                        error: new InvalidResponseError(
                          'Incomplete data returned from placeOrder mutation',
                        ),
                      }
                    : {
                        _tag: 'Right' as const,
                        data: { ...data, isGuest: !authState.isAuthenticated },
                      },
                ),
              ),
            ),
            // build CompletedCart from input cart and response data
            mapRight(({ data }) => ({
              _tag: 'Right' as const,
              data: {
                cart,
                order: data.placeOrder.order,
                completedAt: new Date().toISOString(),
                isGuest: data.isGuest,
              },
            })),
          ),
        ),
      ),
    (effect: PlaceOrderTask['effect']): PlaceOrderTask => ({ effect }),
  )

/**
 * Create task to request placeOrder mutation in conjunction with setPaymentMethodOnCart mutation
 * (for credit card payment method)
 */
export const createPlaceOrderTaskForCreditCard: (
  params: PlaceOrderTaskCart & Pick<PlaceOrderWithPaymentMethodMutationVariables, 'paymentMethod'>,
) => PlaceOrderTask = ramdaPipe(
  (params) => (cartToValidate: CartFragment) =>
    of(
      params.cartId === cartToValidate.id
        ? { _tag: 'Right' as const, data: params }
        : {
            _tag: 'Left' as const,
            error: new UnexpectedCartError(
              `Cart ID differs from state (expected: ${params.cartId}, actual: ${cartToValidate.id})`,
            ),
          },
    ),
  (validateParams) => (cart, dispensarySlug, guestMarketingOptIn, guestOptedInSmsNumber) =>
    validateParams(cart).pipe(
      switchMapRight(({ data: params }) =>
        combineLatest([
          latestApolloClient$,
          // use latestAuthStateWithAccessToken$ to ensure authentication is settled
          latestAuthStateWithAccessToken$,
        ]).pipe(
          take(1),
          switchMap(([client, authState]) =>
            placeOrderWithPaymentMethodFactory(
              client,
              authState.isAuthenticated ? authState.accessToken : undefined,
              {
                ...params,
                input: placeOrderInputFor({
                  cartId: params.cartId,
                  dispensarySlug,
                  isGuest: !authState.isAuthenticated,
                  guestMarketingOptIn,
                  guestOptedInSmsNumber,
                }),
              },
            ).pipe(
              mapRight(({ data }) =>
                !isValidPlaceOrderWithPaymentMethodMutation(data)
                  ? {
                      _tag: 'Left' as const,
                      error: new InvalidResponseError(
                        'Incomplete data returned from placeOrderWithPaymentMethod mutation',
                      ),
                    }
                  : {
                      _tag: 'Right' as const,
                      data: { ...data, isGuest: !authState.isAuthenticated },
                    },
              ),
            ),
          ),
          // build CompletedCart from input cart and response data
          mapRight(({ data }) => ({
            _tag: 'Right' as const,
            data: {
              cart: {
                ...cart,
                ...data.setPaymentMethodOnCart.cart,
              },
              order: data.placeOrder.order,
              completedAt: new Date().toISOString(),
              isGuest: data.isGuest,
            },
          })),
        ),
      ),
    ),
  (effect: PlaceOrderTask['effect']): PlaceOrderTask => ({ effect }),
)
